const ROOT_TYPES = {
  BOOKMARKS_BAR: 'bookmarks_bar',
  OTHER_BOOKMARKS: 'other_bookmarks',
  MOBILE_BOOKMARKS: 'mobile_bookmarks'
};

const ROOT_TYPE_TO_ID = {
  [ROOT_TYPES.BOOKMARKS_BAR]: '1',
  [ROOT_TYPES.OTHER_BOOKMARKS]: '2',
  [ROOT_TYPES.MOBILE_BOOKMARKS]: '3'
};

const ROOT_TYPE_TO_TITLE = {
  [ROOT_TYPES.BOOKMARKS_BAR]: 'Bookmarks Bar',
  [ROOT_TYPES.OTHER_BOOKMARKS]: 'Other Bookmarks',
  [ROOT_TYPES.MOBILE_BOOKMARKS]: 'Mobile Bookmarks'
};

const ROOT_ORDER = [
  ROOT_TYPES.BOOKMARKS_BAR,
  ROOT_TYPES.OTHER_BOOKMARKS,
  ROOT_TYPES.MOBILE_BOOKMARKS
];

const LEGACY_ROOT_TITLES = new Map(
  Object.entries(ROOT_TYPE_TO_TITLE).map(([rootType, title]) => [title.toLowerCase(), rootType])
);

const FALLBACK_ROOT_TYPE = ROOT_TYPES.BOOKMARKS_BAR;

function getRootTypeByNodeId(nodeId) {
  return ROOT_ORDER.find(rootType => ROOT_TYPE_TO_ID[rootType] === String(nodeId)) || null;
}

function getRootNodeId(rootType) {
  return ROOT_TYPE_TO_ID[rootType] || null;
}

function getRootTitle(rootType) {
  return ROOT_TYPE_TO_TITLE[rootType] || 'Bookmarks';
}

function getRootOrderIndex(rootType) {
  const index = ROOT_ORDER.indexOf(rootType);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

function inferRootTypeFromPath(path) {
  if (!path || typeof path !== 'string') {
    return null;
  }

  const [firstSegment] = path.split('/').filter(Boolean);
  if (!firstSegment) {
    return null;
  }

  return LEGACY_ROOT_TITLES.get(firstSegment.toLowerCase()) || null;
}

function stripLegacyRootPrefix(path, rootType) {
  if (!path || typeof path !== 'string') {
    return '';
  }

  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) {
    return '';
  }

  const canonicalRootTitle = getRootTitle(rootType).toLowerCase();
  if (segments[0].toLowerCase() === canonicalRootTitle) {
    return segments.slice(1).join('/');
  }

  return path;
}

function createPermanentRootNode(rootType) {
  return {
    id: getRootNodeId(rootType),
    title: getRootTitle(rootType),
    url: null,
    path: '',
    parentId: null,
    index: getRootOrderIndex(rootType),
    dateAdded: 0,
    lastModified: 0,
    isFolder: true,
    isPermanentRoot: true,
    rootType
  };
}

export function normalizeBookmarkPayload(flatBookmarks) {
  if (!Array.isArray(flatBookmarks)) {
    return [];
  }

  const sourceItems = flatBookmarks
    .filter(item => item && typeof item === 'object')
    .map(item => ({ ...item }));
  const sourceById = new Map(sourceItems.map(item => [String(item.id), item]));
  const rootTypeMemo = new Map();

  function resolveRootType(item) {
    const itemId = String(item.id);
    if (rootTypeMemo.has(itemId)) {
      return rootTypeMemo.get(itemId);
    }

    if (item.rootType && ROOT_TYPE_TO_ID[item.rootType]) {
      rootTypeMemo.set(itemId, item.rootType);
      return item.rootType;
    }

    const explicitRootType = getRootTypeByNodeId(item.id);
    if (explicitRootType) {
      rootTypeMemo.set(itemId, explicitRootType);
      return explicitRootType;
    }

    const parentRootType = getRootTypeByNodeId(item.parentId);
    if (parentRootType) {
      rootTypeMemo.set(itemId, parentRootType);
      return parentRootType;
    }

    const pathRootType = inferRootTypeFromPath(item.path);
    if (pathRootType) {
      rootTypeMemo.set(itemId, pathRootType);
      return pathRootType;
    }

    if (item.parentId && sourceById.has(String(item.parentId))) {
      const inheritedRootType = resolveRootType(sourceById.get(String(item.parentId)));
      rootTypeMemo.set(itemId, inheritedRootType);
      return inheritedRootType;
    }

    rootTypeMemo.set(itemId, item.isPermanentRoot ? null : FALLBACK_ROOT_TYPE);
    return item.isPermanentRoot ? null : FALLBACK_ROOT_TYPE;
  }

  const normalizedById = new Map();
  const usedRootTypes = new Set();

  for (const sourceItem of sourceItems) {
    const itemId = String(sourceItem.id);
    const rootType = resolveRootType(sourceItem);
    const isPermanentRoot = Boolean(sourceItem.isPermanentRoot) || Boolean(getRootTypeByNodeId(sourceItem.id));
    if (rootType) {
      usedRootTypes.add(rootType);
    }

    normalizedById.set(itemId, {
      ...sourceItem,
      id: itemId,
      parentId: isPermanentRoot ? null : (sourceItem.parentId ? String(sourceItem.parentId) : null),
      path: stripLegacyRootPrefix(sourceItem.path || '', rootType),
      isFolder: sourceItem.url ? false : sourceItem.isFolder !== false,
      isPermanentRoot,
      rootType,
      title: isPermanentRoot && rootType ? getRootTitle(rootType) : (sourceItem.title || 'Untitled')
    });
  }

  for (const rootType of usedRootTypes) {
    const rootId = getRootNodeId(rootType);
    if (!normalizedById.has(rootId)) {
      normalizedById.set(rootId, createPermanentRootNode(rootType));
    }
  }

  return Array.from(normalizedById.values()).sort((left, right) => {
    if (Boolean(left.isPermanentRoot) !== Boolean(right.isPermanentRoot)) {
      return left.isPermanentRoot ? -1 : 1;
    }

    if (left.isPermanentRoot && right.isPermanentRoot) {
      return getRootOrderIndex(left.rootType) - getRootOrderIndex(right.rootType);
    }

    if ((left.rootType || '') !== (right.rootType || '')) {
      return getRootOrderIndex(left.rootType) - getRootOrderIndex(right.rootType);
    }

    if ((left.path || '') !== (right.path || '')) {
      return (left.path || '').localeCompare(right.path || '');
    }

    if (Boolean(left.isFolder) !== Boolean(right.isFolder)) {
      return left.isFolder ? -1 : 1;
    }

    if ((left.index || 0) !== (right.index || 0)) {
      return (left.index || 0) - (right.index || 0);
    }

    if ((left.title || '') !== (right.title || '')) {
      return (left.title || '').localeCompare(right.title || '');
    }

    return (left.url || '').localeCompare(right.url || '');
  });
}

function sortTreeItems(nodes) {
  nodes.sort((left, right) => {
    if (Boolean(left.isPermanentRoot) !== Boolean(right.isPermanentRoot)) {
      return left.isPermanentRoot ? -1 : 1;
    }

    if (left.isPermanentRoot && right.isPermanentRoot) {
      return getRootOrderIndex(left.rootType) - getRootOrderIndex(right.rootType);
    }

    if (Boolean(left.isFolder) !== Boolean(right.isFolder)) {
      return left.isFolder ? -1 : 1;
    }

    if ((left.index || 0) !== (right.index || 0)) {
      return (left.index || 0) - (right.index || 0);
    }

    if ((left.title || '') !== (right.title || '')) {
      return (left.title || '').localeCompare(right.title || '');
    }

    return (left.url || '').localeCompare(right.url || '');
  });

  for (const node of nodes) {
    if (node.isFolder && Array.isArray(node.children)) {
      sortTreeItems(node.children);
    }
  }
}

export function reconstructBookmarkTree(flatBookmarks) {
  const normalized = normalizeBookmarkPayload(flatBookmarks).map(item => ({
    ...item,
    children: item.isFolder ? [] : undefined
  }));
  const nodesById = new Map(normalized.map(item => [String(item.id), item]));
  const tree = [];

  for (const item of normalized) {
    if (item.isPermanentRoot) {
      tree.push(item);
      continue;
    }

    const parentId = item.parentId ? String(item.parentId) : null;
    if (parentId && nodesById.has(parentId)) {
      nodesById.get(parentId).children.push(item);
      continue;
    }

    if (item.rootType) {
      const rootNode = nodesById.get(getRootNodeId(item.rootType));
      if (rootNode) {
        rootNode.children.push(item);
        continue;
      }
    }

    tree.push(item);
  }

  sortTreeItems(tree);
  return tree;
}
